'use client'

// React Imports
import { useState } from 'react'

// Next Imports
import { useParams, useRouter, useSearchParams } from 'next/navigation'

// MUI Imports
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'

import { signIn } from 'next-auth/react'
import { Controller, useForm } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { object, minLength, string, pipe, nonEmpty } from 'valibot'
import classnames from 'classnames'
import type { SubmitHandler } from 'react-hook-form'
import type { InferInput } from 'valibot'

import type { Mode } from '@core/types'
import type { Locale } from '@configs/i18n'

import themeConfig from '@configs/themeConfig'

import { useSettings } from '@core/hooks/useSettings'

// Util Imports
import { getLocalizedUrl } from '@/utils/i18n'
import type { LoginSuccessResponse, MfaChallengeResponse } from '@/libs/api/repositories/auth.repository'

type ErrorType = {
  message: string[] | string
}

type FormData = InferInput<typeof schema>

const schema = object({
  username: pipe(string(), minLength(1, 'This field is required')),
  password: pipe(
    string(),
    nonEmpty('This field is required'),
    minLength(6, 'Password must be at least 6 characters long')
  )
})

function isMfaChallenge(res: unknown): res is MfaChallengeResponse {
  const row = res as MfaChallengeResponse
  return !!(row?.mfa_required || row?.mfa_setup_required)
}

function apiErrorMessage(error: any, fallback: string) {
  const data = error?.data
  const msg = data?.message || error?.message || fallback
  return Array.isArray(msg) ? msg.join(', ') : String(msg)
}

const Login = ({ mode }: { mode: Mode }) => {
  // States
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [errorState, setErrorState] = useState<ErrorType | null>(null)
  const [mfa, setMfa] = useState<MfaChallengeResponse | null>(null)
  const [otp, setOtp] = useState('')
  const [mfaSubmitting, setMfaSubmitting] = useState(false)

  const router = useRouter()
  const searchParams = useSearchParams()
  const { lang: locale } = useParams()
  const { settings } = useSettings()

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormData>({
    resolver: valibotResolver(schema),
    defaultValues: {
      username: '',
      password: ''
    }
  })



  const handleDevAutoLogin = async () => {

    setValue('username', 'pmfadmin', { shouldValidate: true })
    setValue('password', '0531886146', { shouldValidate: true })

    await onSubmit({ username: 'pmfadmin', password: '0531886146' } as FormData)
  }

  const characterIllustration = '/images/illustrations/auth/v2-login.png'


  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  const finishLogin = async (backend: LoginSuccessResponse) => {
    const { setApiToken } = await import('@/libs/api')
    const opaque = backend?.data?.access_token
    if (opaque) setApiToken(opaque)

    const res = await signIn('credentials', {
      access_token: opaque,
      redirect: false
    })

    if (!res?.ok || res.error) {
      throw new Error(res?.error || 'Failed to start session')
    }

    const rawRedirect = searchParams.get('redirectTo') ?? '/landing'
    const redirectURL =
      rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') && !rawRedirect.includes('..')
        ? rawRedirect
        : '/landing'

    router.replace(getLocalizedUrl(redirectURL, locale as Locale))
    router.refresh()
  }

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    try {
      setErrorState(null)
      const { authRepository } = await import('@/libs/api')
      const backend = await authRepository.login({
        username: data.username,
        password: data.password
      })

      if (isMfaChallenge(backend)) {
        setMfa(backend)
        setOtp('')
        return
      }

      await finishLogin(backend)
    } catch (error: any) {
      setErrorState({ message: [apiErrorMessage(error, 'An unexpected error occurred. Please try again.')] })
    }
  }

  const onVerifyMfa = async () => {
    if (!mfa?.mfa_token) return
    try {
      setErrorState(null)
      setMfaSubmitting(true)
      const { authRepository } = await import('@/libs/api')
      const backend = await authRepository.verifyMfa({
        mfa_token: mfa.mfa_token,
        code: otp.trim()
      })
      await finishLogin(backend)
    } catch (error: any) {
      setErrorState({ message: [apiErrorMessage(error, 'Invalid authenticator code')] })
    } finally {
      setMfaSubmitting(false)
    }
  }

  return (
    <div className='flex bs-full justify-center'>
      <div
        className={classnames(
          'flex flex-1 relative max-md:hidden', // Removed bs-full
          {
            'border-ie': settings.skin === 'bordered'
          }
        )}
      >
        <div className='flex justify-center items-center flex-1 max-md:hidden'>
          <img
            src={characterIllustration}
            alt='character-illustration'
            className='w-full h-full object-cover object-left'
          />
        </div>
      </div>

      <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
        {/* <div className='absolute block-start-5 sm:block-start-[33px] inline-start-6 sm:inline-start-[38px]'>
          <Logo />
        </div> */}
        <div className='flex flex-col gap-5 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset]'>
          <div>
            <Typography variant='h4'>
              {`Welcome to ${themeConfig.templateName}!`}
              <span
                role='button'
                tabIndex={0}
                onDoubleClick={handleDevAutoLogin}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleDevAutoLogin()
                }}
                aria-label='Dev auto-login'
              >
                👋🏻
              </span>
            </Typography>
            <Typography>
              {mfa
                ? (mfa.mfa_setup_required
                  ? 'Set up authenticator to continue'
                  : 'Enter your authenticator code')
                : 'Please sign-in to your account and start the adventure'}
            </Typography>
          </div>
          {errorState && (
            <Alert severity='error' onClose={() => setErrorState(null)}>
              {(Array.isArray(errorState.message) ? errorState.message : [errorState.message]).map((msg, index) => (
                <Typography key={`${msg}-${index}`} variant='body2'>
                  {msg}
                </Typography>
              ))}
            </Alert>
          )}

          {mfa ? (
            <div className='flex flex-col gap-5'>
              {mfa.mfa_setup_required && (
                <>
                  <Alert severity='info'>
                    Install Google Authenticator or Microsoft Authenticator, then scan the QR or enter the setup key.
                  </Alert>
                  {mfa.qr_data_url && (
                    <div className='flex justify-center'>
                      <img src={mfa.qr_data_url} alt='Authenticator QR' width={180} height={180} />
                    </div>
                  )}
                  {mfa.mfa_secret && (
                    <TextField
                      size='small'
                      label='Setup key'
                      value={mfa.mfa_secret}
                      InputProps={{ readOnly: true }}
                      helperText='If you cannot scan, add this key manually in the app'
                    />
                  )}
                </>
              )}
              <TextField
                autoFocus
                fullWidth
                label='Authenticator code'
                value={otp}
                onChange={e => {
                  setOtp(e.target.value.replaceAll(/\D/g, '').slice(0, 6))
                  errorState !== null && setErrorState(null)
                }}
                inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
              />
              <Button fullWidth variant='contained' onClick={onVerifyMfa} disabled={mfaSubmitting || otp.length !== 6}>
                {mfaSubmitting ? 'Verifying…' : 'Verify'}
              </Button>
              <Button
                fullWidth
                variant='text'
                onClick={() => {
                  setMfa(null)
                  setOtp('')
                  setErrorState(null)
                }}
              >
                Back to login
              </Button>
            </div>
          ) : (
          <form
            noValidate
            action={() => { }}
            autoComplete='off'
            onSubmit={handleSubmit(onSubmit)}
            className='flex flex-col gap-5'
          >
            <Controller
              name='username'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  id='login-username'
                  fullWidth
                  autoFocus
                  type='text'
                  label='Username'
                  onChange={e => {
                    const value = e.target.value.replaceAll(/\s+/g, '');
                    field.onChange(value)
                    errorState !== null && setErrorState(null)
                  }}
                  {...((errors.username || errorState !== null) && {
                    error: true,
                    helperText: errors?.username?.message || errorState?.message[0]
                  })}
                />
              )}
            />
            <Controller
              name='password'
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label='Password'
                  id='login-password'
                  type={isPasswordShown ? 'text' : 'password'}
                  onChange={e => {
                    const value = e.target.value.replaceAll(/\s+/g, '');
                    field.onChange(value)
                    errorState !== null && setErrorState(null)
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            size='small'
                            edge='end'
                            onClick={handleClickShowPassword}
                            onMouseDown={e => e.preventDefault()}
                            aria-label='toggle password visibility'
                          >
                            <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                  {...(errors.password && { error: true, helperText: errors.password.message })}
                />
              )}
            />
            {/* <div className='flex justify-between items-center flex-wrap gap-x-3 gap-y-1'>
              <FormControlLabel control={<Checkbox defaultChecked />} label='Remember me' />
              <Typography className='text-end' color='primary.main' component={Link} href='/forgot-password'>
                Forgot password?
              </Typography>
            </div> */}
            <Button fullWidth variant='contained' type='submit' disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : 'Log In'}
            </Button>

          </form>
          )}

        </div>
      </div>
    </div>
  )
}

export default Login
